import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { ARTICLES, ARTICLE_BY_SLUG } from "../../content/articles";
import { OFFERS } from "../../content/site";
import { useSeo, ORG_LD } from "../../lib/seo";
import { NotFound } from "./Pages";

const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export function Conseils() {
  useSeo({ title: "Conseils : broderie, textile et personnalisation", description: "Guides pratiques de l'Atelier KOUOH : choisir sa technique de marquage, le grammage d'un t-shirt, les textiles écologiques, la broderie de casquettes, la broderie en direct.", path: "/conseils" });
  return (
    <section className="k-section pt-12">
      <p className="k-eyebrow">Conseils</p>
      <h1 className="k-h1 mt-5 max-w-3xl">Bien choisir avant de personnaliser.</h1>
      <p className="k-lead mt-6 max-w-2xl">Techniques, textiles, grammages, casquettes, événements : nos guides pour préparer un projet qui tient dans le temps.</p>
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="conseils-grid">
        {ARTICLES.map((a) => (
          <Link key={a.slug} to={`/conseils/${a.slug}`} className="k-card p-6 flex flex-col hover:border-[#C25E38]" data-testid={`article-card-${a.slug}`}>
            <span className="k-eyebrow">{a.eyebrow}</span>
            <h2 className="font-display text-2xl mt-3">{a.title}</h2>
            <p className="text-sm text-[#57534E] mt-3 leading-relaxed line-clamp-3">{a.desc}</p>
            <span className="text-xs uppercase tracking-widest mt-auto pt-5">Lire →</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const Bullets = ({ items, tone }) => (
  <ul className={`mt-3 grid gap-1.5 text-sm leading-relaxed ${tone === "con" ? "text-[#8C857B]" : "text-[#44403c]"}`}>
    {items.map((x) => <li key={x} className="flex gap-2"><span className={tone === "con" ? "text-[#A84C2A]" : "text-[#C25E38]"}>{tone === "con" ? "–" : "+"}</span>{x}</li>)}
  </ul>
);

export function Article() {
  const { slug } = useParams();
  const a = ARTICLE_BY_SLUG[slug];
  const ld = a && { "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.desc, datePublished: a.date, author: { "@type": "Person", name: "Johan Kouoh" }, publisher: ORG_LD, mainEntityOfPage: `/conseils/${a.slug}` };
  useSeo({ title: a?.seoTitle || "Page introuvable", description: a?.desc, path: `/conseils/${slug}`, jsonLd: ld || null });
  if (!a) return <NotFound />;
  const offer = OFFERS[a.offer];
  const others = ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3);
  return (
    <article className="k-section pt-12" data-testid="article-page">
      <Link to="/conseils" className="k-link text-sm inline-flex items-center gap-2"><ArrowLeft size={14} /> Tous les conseils</Link>
      <div className="grid lg:grid-cols-12 gap-12 mt-8">
        <div className="lg:col-span-8 max-w-3xl">
          <p className="k-eyebrow">{a.eyebrow} · {fmt(a.date)}</p>
          <h1 className="k-h1 mt-5" data-testid="article-title">{a.title}</h1>
          <p className="k-lead mt-6">{a.intro}</p>
          <div className="mt-10 grid gap-8">
            {a.sections.map((s) => (
              <section key={s.h} className="border-t border-[#E8E2D8] pt-6">
                <h2 className="font-display text-2xl">{s.h}</h2>
                {s.p && <p className="text-[#44403c] mt-3 leading-relaxed">{s.p}</p>}
                {s.list && <Bullets items={s.list} />}
                {(s.pros || s.cons) && <div className="grid sm:grid-cols-2 gap-4">{s.pros && <Bullets items={s.pros} />}{s.cons && <Bullets items={s.cons} tone="con" />}</div>}
              </section>
            ))}
          </div>
          <p className="text-xs text-[#8C857B] mt-10">Écrit par Johan Kouoh, Atelier KOUOH — Nice.</p>
        </div>
        <aside className="lg:col-span-4 grid gap-6 content-start">
          <div className="bg-[#1C1917] text-[#FBF9F5] p-6">
            <p className="k-eyebrow">Passer à l'action</p>
            <p className="font-display text-2xl mt-3">{offer.tagline}</p>
            <p className="text-sm text-[#D6CEBF] mt-3 leading-relaxed">{offer.short}</p>
            <Link to={`/demande/${a.offer}`} className="k-btn mt-6 bg-[#C25E38] border-[#C25E38] w-full justify-center" data-testid="article-cta">{offer.cta} <ArrowRight size={16} /></Link>
          </div>
          <div>
            <p className="k-eyebrow mb-3">À lire aussi</p>
            <div className="grid gap-3">{others.map((o) => <Link key={o.slug} to={`/conseils/${o.slug}`} className="k-card p-4 text-sm hover:border-[#C25E38]">{o.title}</Link>)}</div>
          </div>
        </aside>
      </div>
    </article>
  );
}
